import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { PhotoAppelComponent } from './photo-appel.component';
import { LucideAngularModule, CalendarClock, Video, Phone, Users } from 'lucide-angular';
import { CallKind } from '../../../core/models/call.model';
import { TranslationService } from '../../../core/services/translation.service';
import { degradeAvatar, initiale } from './avatars-appel';

/**
 * Ecran « Appel termine » — maquette « Appels — palette claire », ecran 7.
 *
 * N'affiche que ce que le poste sait : la duree, l'heure de debut, le type, et qui etait
 * la (cette personne, l'interlocuteur, ceux invites depuis ce poste), avec leur photo.
 * Clair par defaut, sombre sous `html.dark`.
 *
 * ABSENT DE LA MAQUETTE, VOLONTAIREMENT :
 *  - la duree de presence de chaque participant (« 10:22 min ») : seul l'appel entier est
 *    chronometre ;
 *  - le sous-titre « Caisse principale » : rien n'indique d'ou l'appel a eu lieu.
 */
@Component({
    selector: 'app-appel-termine',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LucideAngularModule, PhotoAppelComponent],
    template: `
    <div class="fixed inset-0 z-50 overflow-hidden text-surface-900 dark:text-[#f4f4fe] bg-gradient-to-b from-white to-surface-50
                dark:bg-[radial-gradient(130%_95%_at_15%_-10%,#251f66_0%,#150f40_34%,#0a0826_62%,#020103_100%)]"
         role="dialog" aria-modal="true" [attr.aria-label]="t().calls.call_ended">
      <div class="lueur absolute pointer-events-none hidden dark:block" aria-hidden="true"></div>

      <div class="relative z-[1] h-full flex flex-col">
        <div class="flex-none flex flex-col items-center px-6 pt-16">
          <div class="coche w-16 h-16 rounded-full flex items-center justify-center mb-[18px] border bg-success-50 border-success-200 text-success-600
                      dark:border-0 dark:bg-[rgba(74,222,128,0.14)] dark:text-[#4ade80]" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
          </div>
          <div class="text-[19px] leading-[normal] font-extrabold">{{ t().calls.call_ended }}</div>
          <div class="text-[13px] leading-[normal] mt-1 text-surface-500 dark:text-[#a5a1d6]">{{ nom() }}</div>
        </div>

        <div class="flex-none mx-5 mt-7 rounded-3xl overflow-hidden border bg-white border-surface-200
                    dark:bg-white/[0.05] dark:border-white/[0.08] dark:backdrop-blur-[10px]">
          <div class="px-5 py-[22px] text-center border-b border-surface-100 dark:border-white/[0.07]">
            <div class="text-[11px] font-bold uppercase tracking-[0.08em] text-surface-500 dark:text-[#a5a1d6]">{{ t().calls.total_duration }}</div>
            <div class="text-[34px] font-extrabold tracking-[-0.02em] mt-1 tabular-nums">{{ duree() }}</div>
          </div>

          <div class="px-5 py-4 flex flex-col gap-3.5">
            <div class="flex items-center gap-3">
              <span class="w-[34px] h-[34px] rounded-[10px] flex items-center justify-center flex-none bg-primary-50 text-primary-700 dark:bg-[rgba(129,140,248,0.14)] dark:text-[#a5b4fc]">
                <lucide-icon [img]="CalendarClock" [size]="16" aria-hidden="true"></lucide-icon>
              </span>
              <div>
                <div class="text-[11px] text-surface-500 dark:text-[#a5a1d6]">{{ t().calls.started_at }}</div>
                <div class="text-sm font-semibold">{{ debutTexte() }}</div>
              </div>
            </div>
            <div class="flex items-center gap-3">
              <span class="w-[34px] h-[34px] rounded-[10px] flex items-center justify-center flex-none bg-primary-50 text-primary-700 dark:bg-[rgba(129,140,248,0.14)] dark:text-[#a5b4fc]">
                <lucide-icon [img]="iconeType()" [size]="16" aria-hidden="true"></lucide-icon>
              </span>
              <div>
                <div class="text-[11px] text-surface-500 dark:text-[#a5a1d6]">{{ t().calls.call_type }}</div>
                <div class="text-sm font-semibold">{{ typeTexte() }}</div>
              </div>
            </div>
          </div>

          <div class="px-5 pt-1 pb-5">
            <div class="text-[11px] font-bold uppercase tracking-[0.06em] mb-2.5 text-surface-500 dark:text-[#a5a1d6]">
              {{ titreParticipants() }}
            </div>
            @for (p of participants(); track $index) {
              <div class="flex items-center gap-2.5 py-2">
                <span class="avatar-ligne w-[34px] h-[34px] rounded-full overflow-hidden flex items-center justify-center text-xs font-bold flex-none border bg-surface-100 border-surface-200 text-surface-700
                             dark:border-0 dark:text-white" [class]="p.moi ? 'bg-primary-50 border-primary-200 text-primary-800' : ''" [style.--degrade]="p.degrade">
                  <app-photo-appel [photo]="p.photo" [nom]="p.nom" [initiales]="p.initiale" />
                </span>
                <span class="flex-1 text-[13.5px] font-semibold truncate">{{ p.nom }}</span>
                @if (p.moi) {
                  <span class="text-[11px] text-surface-500 dark:text-[#a5a1d6]">{{ t().calls.you }}</span>
                }
              </div>
            }
          </div>
        </div>

        <div class="flex-1"></div>

        <div class="flex-none px-5 flex flex-row gap-2.5" style="padding-bottom:calc(env(safe-area-inset-bottom,0px) + 28px)">
          <button type="button" (click)="fermer.emit()" class="bouton flex-1 min-h-12 rounded-full text-sm font-semibold border bg-white border-surface-300 text-surface-700
                  dark:bg-white/[0.07] dark:border-white/10 dark:text-[#f4f4fe]">
            {{ t().calls.close }}
          </button>
          <button type="button" (click)="rappeler.emit()" class="bouton flex-1 min-h-12 rounded-full text-[14.5px] font-bold border bg-primary-50 border-primary-200 text-primary-800
                  dark:border-0 dark:text-white dark:bg-[linear-gradient(160deg,#a5b4fc,#4f46e5)] dark:shadow-[0_14px_28px_-10px_rgba(79,70,229,0.55)]">
            {{ t().calls.call_back }}
          </button>
        </div>
      </div>
    </div>
  `,
    styles: [
        `
      .lueur { top:-130px; right:-100px; width:340px; height:340px; border-radius:9999px;
        background:radial-gradient(circle,rgba(52,211,153,0.14) 0%,rgba(52,211,153,0) 70%); filter:blur(4px);
        animation:derive 16s ease-in-out infinite; }
      :host-context(.dark) .avatar-ligne { background:var(--degrade); }
      .coche { animation:entree-coche .5s cubic-bezier(.34,1.56,.64,1); }
      .bouton { transition:transform .15s ease; }
      .bouton:active { transform:scale(0.96); }
      @keyframes derive { 0%,100% { transform:translate(0,0) scale(1); } 50% { transform:translate(-18px,14px) scale(1.06); } }
      @keyframes entree-coche { 0% { transform:scale(0); opacity:0; } 60% { transform:scale(1.15); } 100% { transform:scale(1); opacity:1; } }
      @media (prefers-reduced-motion: reduce) { .lueur, .coche { animation:none; } }
    `,
    ],
})
export class AppelTermineComponent {
    /** L'interlocuteur de l'appel. */
    readonly nom = input.required<string>();
    readonly duree = input.required<string>();
    /** Debut de l'appel, en millisecondes (epoch). */
    readonly debut = input.required<number>();
    readonly type = input.required<CallKind>();
    /** Noms des personnes invitees depuis CE poste pendant l'appel. */
    readonly invites = input.required<string[]>();
    /** Nom de la personne connectee. */
    readonly moi = input.required<string>();
    /** Photos de profil connues, par nom : la photo remplace les initiales, qui restent le repli. */
    readonly photos = input<Record<string, string>>({});

    readonly fermer = output<void>();
    readonly rappeler = output<void>();

    private readonly translation = inject(TranslationService);
    readonly t = this.translation.t;

    readonly CalendarClock = CalendarClock;

    readonly iconeType = computed(() => (this.type() === 'video' ? Video : this.type() === 'group' ? Users : Phone));

    readonly typeTexte = computed(() => {
        const c = this.t().calls;
        return this.type() === 'video' ? c.type_video : this.type() === 'group' ? c.type_group : c.type_audio;
    });

    /** « Aujourd'hui, 14:32 » le jour meme, sinon « 14:19 · 29 septembre », dans la langue choisie. */
    readonly debutTexte = computed(() => {
        const date = new Date(this.debut());
        const locale = this.translation.locale();
        const heure = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
        if (date.toDateString() === new Date().toDateString()) return `${this.t().calls.today}, ${heure}`;
        const jour = date.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
        return `${heure} · ${jour}`;
    });

    readonly titreParticipants = computed(() => this.t().calls.participants_count.replace('{n}', String(this.participants().length)));

    /** Soi-meme en premier, puis l'interlocuteur, puis ceux invites dans l'ordre d'invitation. */
    readonly participants = computed(() => {
        const ligne = (nom: string, moi = false) => ({ nom, moi, initiale: initiale(nom), degrade: degradeAvatar(nom), photo: this.photos()[nom] });
        const moi = this.moi().trim();
        return [...(moi ? [ligne(moi, true)] : []), ligne(this.nom()), ...this.invites().map((n) => ligne(n))];
    });
}
